<template>
  <v-list>
    <v-list-item v-if="isURL === false && isZIP === false"
                 @click.prevent="launchPresentation(false)">
      <v-list-item-title>
        Open in this tab
      </v-list-item-title>
    </v-list-item>
    <v-list-item v-if="isZIP"
                 @click.prevent="downloadZipFilePresentation">
      <v-list-item-title>
        Download ZIP File
      </v-list-item-title>
    </v-list-item>
    <v-list-item v-if="isZIP === false"
                 @click.prevent="launchPresentation(true)">
      <v-list-item-title>
        Open in a new tab
      </v-list-item-title>
    </v-list-item>
    <v-list-item v-if="canUseExcel"
                 @click.prevent="launchExcelPresentation(false)">
      <v-list-item-title>
        Open in Excel
      </v-list-item-title>
    </v-list-item>
    <v-list-item v-if="canUsePowerPoint"
                 @click.prevent="launchPowerPointPresentation(false)">
      <v-list-item-title>
        Open in PowerPoint
      </v-list-item-title>
    </v-list-item>
    <v-list-item v-if="canUseOnlyOffice"
                 @click.prevent="launchOnlyOffice(false)">
      <v-list-item-title>
        Open in presentation mode
      </v-list-item-title>
    </v-list-item>
  </v-list>
</template>
<script>
import {mapGetters} from 'vuex';
import {find} from "lodash";
import contentService from "../../../services/contentService";
import bugsnagService from '../../../services/bugsnagService';
import presentationService from '../../../services/presentationService';

export default {
  props: {
    presentation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      files: [],
    }
  },
  computed: {
    ...mapGetters([
      'agent', // MLD-3019
      'isAdmin',
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'liveMode', // MLD-3158
      'mustSelectCustomer', // MLD-5823
    ]),
    canUseExcel() { // MLD-3040
      return this.agent
        && (this.agent.is_chrome || this.agent.is_edge) // Safari sometimes appears as desktop so I need to check this instead
        && this.agent.is_desktop
        && this.team
        && this.team.excel_enabled
        && this.presentation.type_excel;
    },
    canUsePowerPoint() { // MLD-3019
      return this.agent
        && (this.agent.is_chrome || this.agent.is_edge) // Safari sometimes appears as desktop so I need to check this instead
        && this.agent.is_desktop
        && this.team
        && this.team.powerpoint_enabled
        && this.presentation.type_powerpoint;
    },
    canUseOnlyOffice() { // MLD-2908
      return this.team
        && this.team.onlyoffice_enabled
        && (this.presentation.type_excel || this.presentation.type_powerpoint);
    },
    isURL() {
      return this.presentation.type_url;
    },
    isHTML() {
      return this.presentation.type_html;
    },
    isZIP() { // MLD-5606
      return this.presentation.type_zip;
    },
    sendActivityToCRM() { // MLD-6770
      return this.presentation
        && this.presentation.send_activity_to_crm;
    },
    isInternalUseOnly() { // MLD-6877
      return this.presentation
        && this.presentation.is_internal_use_only;
    },
    mustSelectCustomerForThisPresentation() { // MLD-6877
      return this.isInternalUseOnly === false
        && this.sendActivityToCRM
        && this.mustSelectCustomer
    },
  },
  methods: {
    async getPresentationFiles() { // MLD-5021
      this.files = await presentationService.getPresentationFiles(this.presentation.id);
    },
    async launchExcelPresentation(newTab) { // MLD-3019
      if (this.mustSelectCustomerForThisPresentation) { // MLD-5823
        await this.$store.dispatch('content/openCustomerPickerDialog');
        return;
      }
      await this.getPresentationFiles(); // MLD-5021
      // @see https://docs.microsoft.com/en-us/office/client-developer/office-uri-schemes#appendix-b---uri-scheme-registration-template-for-ms-powerpoint-scheme
      const pptxFile = find(this.files, f => (f.path === 'presentation.xlsx' || f.path === 'presentation.xls'));
      if (pptxFile) {
        const data = {
          live_mode: this.liveMode,
          payload: {
            is_excel: true,
            use_onlyoffice: false,
          }
        }
        await contentService.launchPresentation(this.presentation, newTab, this.$router, data);
        await contentService.openExcelFile(this.presentation, pptxFile);
        this.close();
      } else {
        this.$toast.warning('The Excel file was not found');
      }
    },
    async launchPowerPointPresentation(newTab) { // MLD-3019
      if (this.mustSelectCustomerForThisPresentation) { // MLD-5823
        await this.$store.dispatch('content/openCustomerPickerDialog');
        return;
      }
      await this.getPresentationFiles(); // MLD-5021
      // @see https://docs.microsoft.com/en-us/office/client-developer/office-uri-schemes#appendix-b---uri-scheme-registration-template-for-ms-powerpoint-scheme
      const pptxFile = find(this.files, f => (f.path === 'presentation.pptx' || f.path === 'presentation.ppt'));
      if (pptxFile) {
        const data = {
          live_mode: this.liveMode,
          payload: {
            is_powerpoint: true,
            use_onlyoffice: false,
          }
        }
        await contentService.launchPresentation(this.presentation, newTab, this.$router, data);
        await contentService.openPowerPointFile(this.presentation, pptxFile);
        this.close();
      } else {
        this.$toast.warning('The PowerPoint file was not found');
      }
    },
    async launchOnlyOffice(newTab) { // MLD-2908
      if (this.mustSelectCustomerForThisPresentation) { // MLD-5823
        await this.$store.dispatch('content/openCustomerPickerDialog');
        return;
      }
      const data = {
        live_mode: this.liveMode,
        payload: {
          is_powerpoint: false,
          use_onlyoffice: true,
        }
      }
      await contentService.launchPresentation(this.presentation, newTab, this.$router, data);
      this.close();
    },
    async downloadZipFilePresentation() { // MLD-5606
      await contentService.downloadZipFilePresentation(this.presentation);
    },
    async launchPresentation(newTab) {
      if (this.mustSelectCustomerForThisPresentation) { // MLD-5823
        await this.$store.dispatch('content/openCustomerPickerDialog');
        return;
      }
      try {
        if (this.presentation.type_zip) {
          return this.downloadZipFilePresentation();
        }
        const data = {
          live_mode: this.liveMode,
          payload: {
            is_powerpoint: false,
            use_onlyoffice: false,
          }
        }
        await contentService.launchPresentation(this.presentation, newTab, this.$router, data);
        this.close();
      } catch (e) {
        const message = `${this.user.email} (${this.user.id}) encountered this error opening presentation ${this.presentation.id} in the browser: ${e.message}`;
        const error = new Error(message);
        error.cause = e;
        bugsnagService.notify(error);
      }
    },
    close() {
      this.$store.dispatch('closeDrawer').then(); // MLD-5641
      // MLD-67565 I commented this out.
      //this.$store.dispatch('content/setBypassCustomerSelection', false); // MLD-5823
      this.$emit('dismiss');
    }
  }
}
</script>
